import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  className?: string;
  align?: "left" | "center";
  iconStyle?: "light" | "gradient";
};

/**
 * Canonical feature grid card — light-fuchsia icon square, left-aligned copy.
 * Use for 2x2 / 3x3 capability grids. For How-It-Works numbered steps use StepCard instead.
 * `align="center"` centres the icon/heading/copy; `iconStyle="gradient"` swaps the light
 * fuchsia square for the purple→fuchsia gradient square used on ComplyBot.
 */
export const FeatureCard = ({
  icon: Icon,
  title,
  children,
  className,
  align = "left",
  iconStyle = "light",
}: Props) => {
  const centered = align === "center";
  const gradient = iconStyle === "gradient";
  return (
    <div
      className={cn(
        "bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
        centered ? "flex flex-col items-center text-center" : "text-left",
        className,
      )}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center mb-4",
          centered && "mx-auto",
        )}
        style={
          gradient
            ? { background: "linear-gradient(135deg, #7130A0, #ED1878)" }
            : { backgroundColor: "rgba(237, 24, 120, 0.1)" }
        }
      >
        <Icon
          className="w-5 h-5"
          style={{ color: gradient ? "#FFFFFF" : "#ED1878" }}
          aria-hidden="true"
        />
      </div>
      <h3 className="font-heading text-ch-purple text-lg mb-2">{title}</h3>
      <p className="font-carlito text-gray-700 text-sm leading-relaxed">{children}</p>
    </div>
  );
};

export default FeatureCard;